import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import { ReservationLedger } from './reservation-ledger';
import { SharedService } from './../../../_shared/shared.service'

@Injectable({ providedIn: 'root' })
export class ReservationLedgerService {

	statuses: any = [
		{ value: 1, name: 'UNLOCKED' },
		{ value: 2, name: 'LOCKED' },
		{ value: 3, name: 'FINAL' }
	];

	ledgerItemTypes: any = [
		{ value: 1, name: 'Order Product' },
		{ value: 2, name: 'Order Tax' }
	];

	payToDetails: any = [
		{ value: 0, name: 'Default' },
		{ value: 1, name: 'Guest' },
		{ value: 2, name: 'KBM' },
		{ value: 3, name: 'Homeowner' },
		{ value: 4, name: 'Vendor' },
		{ value: 5, name: 'Travel Agent' },
		{ value: 6, name: 'Rental Car' },
		{ value: 7, name: 'Housekeeping' },
		{ value: 8, name: 'Resort' },
	];


	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService
		) { }

	urlEndpoint = 'reservation-ledgers';

	add(reservationLedger: ReservationLedger): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(reservationLedger));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, reservationLedger);
	}

	update(reservationLedger: ReservationLedger): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, reservationLedger);
	}

  updateLedgerTax(reservationLedger: ReservationLedger): Observable<ApiResponse> {
    return this.http.patch<ApiResponse>(`${environment.apiUrl}/update-reservation-ledgers-tax`, reservationLedger);
  }

	delete(developmentId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${developmentId}`);
	}

	deleteAllByReservationId(reservationId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/reservation-all/${reservationId}`);
	}

	findAll(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		console.log('posting... ' + JSON.stringify(filters));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));

	}

	findSummaryByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		console.log('posting... ' + JSON.stringify(filters));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/summary`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));

	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	generateLedgers(reservationId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/generate-ledger/${reservationId}`);
	}
	reGenerateNetIncome(reservationId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/regenerate-net-income/${reservationId}`);
	}
  reGenerateTax(reservationId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/regenerate-taxes/${reservationId}`);
  }
	syncRuCreditVoucher(ruReservationId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/rentals-united/sync-reservations-payment/${ruReservationId}`);
	}

	toggleLedgerItemsLock(reservationLedgerId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}-item-toggle-lock/${reservationLedgerId}`);
	}

	lockLedger(reservationId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}-lock/${reservationId}`);
	}

	unlockLedger(reservationId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}-unlock/${reservationId}`);
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findAllOrderProducts(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/order-products`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findLedgerOrderProduct(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/order-products/ledger/list`);
	}

	findExpenseOrderProduct(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/order-products/expense/list`);
	}

	findAllChartAccounts(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/chartAccounts`).pipe(retry({ count: 2, delay: 1500 }));
	}

	generateBulkStatement(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/owner-stmt-rez/add-reservation-into-stmt`, data);
  }

  previewBulkStatement(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/owner-stmt-rez/preview-reservation-in-stmt`, data);
  }


  addTravelAgent(data: any): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(data));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/reservations/add-travel-agent-fee`, data);
	}

	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any) {
		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
}
